import { captureException } from "@/lib/sentry";
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

export const initGA = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

  if (!measurementId) {
    console.warn('Missing required Google Analytics key: VITE_GA_MEASUREMENT_ID');
    return;
  }

  initGAWithId(measurementId);
};

export const initGAWithId = (measurementId: string) => {
  if (!measurementId) return;
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`)) return;

  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}', { send_page_view: false });
  `;
  document.head.appendChild(script2);
};

/**
 * Hardcoded fallback Meta Pixel ID.
 *
 * Hardcoded fallback used if VITE_META_PIXEL_ID is absent from the build
 * environment (e.g. a staging build that skips env injection). Without this,
 * Meta Pixel tracking would be silently dropped.
 *
 * @aec004-db-managed: pixel ID is runtime-configurable via ngu_config.meta_pixel_id
 * (set through the NGU admin tracking config UI). The DB row is the primary source;
 * this fallback is a last-resort for when the DB is unavailable. If the pixel ID has
 * not yet been configured in Meta Business Suite, set VITE_META_PIXEL_ID env var
 * or populate this constant with the 15-16 digit Pixel ID from Meta → Events Manager.
 */
const FALLBACK_META_PIXEL_ID = ""; // @aec004-db-managed

/**
 * Initializes the Meta pixel base code.
 * Must be called before any fbq("track", ...) calls.
 * Reads VITE_META_PIXEL_ID at build time; falls back to FALLBACK_META_PIXEL_ID
 * so a misconfigured build environment degrades to real tracking rather than
 * silently dropping all pixel events.
 */
export const initMetaPixel = () => {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID || FALLBACK_META_PIXEL_ID;
  if (!pixelId) {
    console.warn('Missing required Meta Pixel key: VITE_META_PIXEL_ID — Meta Pixel will not initialize');
    return;
  }
  initMetaPixelWithId(pixelId);
};

export const initMetaPixelWithId = (pixelId: string) => {
  if (!pixelId) return;
  if (typeof window !== "undefined" && typeof window.fbq === "function") return;

  const script = document.createElement("script");
  script.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  const noscript = document.createElement("noscript");
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
};

function redactAnalyticsPath(url: string): string {
  return url.replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, '/:token');
}

let warnedMissingGaId = false;

export const trackPageView = (url: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) {
    if (!warnedMissingGaId) {
      warnedMissingGaId = true;
      console.warn(
        'trackPageView: VITE_GA_MEASUREMENT_ID is not set — GA4 page_view events will not be sent. ' +
        'Set VITE_GA_MEASUREMENT_ID in the build environment.'
      );
    }
    return;
  }

  const safePath = redactAnalyticsPath(url);
  // Use gtag('event', 'page_view') instead of gtag('config') so we can
  // explicitly control page_location and prevent GA from falling back to
  // window.location.href (which may contain bearer tokens in path segments).
  window.gtag('event', 'page_view', {
    page_path: safePath,
    page_location: window.location.origin + safePath,
    page_title: document.title,
    send_to: measurementId,
  });
};

/**
 * Google Ads delayed-navigation conversion helper.
 *
 * Fires a `DialedNumber` conversion event (the event name configured in
 * the linked Google Ads account). If a URL is supplied the browser
 * navigates there after the event fires or after the 2-second fallback
 * timer — whichever fires first. Calling without a URL (form submits)
 * records the intent signal only and does not navigate.
 *
 * Pass the Google Ads conversion label (format: "AW-XXXXXXXXXX/YYYYYYYYYYY")
 * as `sendTo` so the event is routed to the correct conversion action even
 * when multiple Google Ads accounts or GA4 properties are linked. When
 * omitted the event falls back to the global gtag config destination.
 *
 * The independent setTimeout ensures navigation always happens even when
 * gtag.js is blocked by an ad blocker or network failure — the inline
 * snippet in index.html always defines window.gtag as a stub, so we
 * cannot rely on a typeof check alone for the fallback.
 */
export function gtagSendEvent(url?: string, sendTo?: string | null): void {
  const navigate = () => {
    if (typeof url === "string") {
      window.location.href = url;
    }
  };

  // Navigate immediately when gtag is completely absent (SSR, consent-blocked
  // before even the inline stub runs, etc.)
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    navigate();
    return;
  }

  // gtag stub is present but gtag.js may still be blocked by an ad blocker
  // or network failure — use an independent timer so navigation always fires
  let navigated = false;
  const safeNavigate = () => {
    if (!navigated) {
      navigated = true;
      navigate();
    }
  };

  const timer = setTimeout(safeNavigate, 2000);
  window.gtag("event", "DialedNumber", {
    ...(sendTo ? { send_to: sendTo } : {}),
    event_callback: () => { clearTimeout(timer); safeNavigate(); },
    event_timeout: 2000,
  });
}

export const trackEvent = (
  action: string,
  category?: string,
  label?: string,
  value?: number
) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  let subscriberId: string | undefined;
  try {
    const raw = localStorage.getItem('mm_attribution');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.subscriber_id) subscriberId = String(parsed.subscriber_id);
    }
  } catch (_swallowedErr) { captureException(_swallowedErr); }

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
    ...(subscriberId ? { subscriber_id: subscriberId } : {}),
  });
};
